import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
const _excluded = ["error", "clearable", "getValidationMessage", "icon", "onChange", "placeholder", "readOnly", "value", "onKeyDown", "styled", "inputRef", "isDisplayOptimized"];
import unescapedText from 'I18n/utils/unescapedText';
import { memo, useEffect, useState, useRef, forwardRef } from 'react';
import { syntheticEvent } from '../../utils/SyntheticEvent/SyntheticEvent';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { callIfPossible } from '../../utils/functions/functions';
import { TextInput } from '../../components/TextInput/TextInput';
import { lazyEval } from '../../utils/lazyEval';
import { computeMinimumSearchMessageOverflow } from '../../utils/computeMinimumSearchMessageOverflow';
import styled, { css } from 'foundations-theming/utils';
import { Clickable } from '../Clickable/Clickable';
import { StyledInput } from '../TextInput/TextInput';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import ShareInput from '../ShareInput/ShareInput';
import { Controllable } from '../Controllable';
import { getFocusStyle, getFocusStyleObject } from '../../utils/focus/focus.style';
import { RemoveIcon } from 'foundations-assets/icons/components/RemoveIcon';
import { SearchIcon } from 'foundations-assets/icons/components/SearchIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getMinimumSearchMessage = (value, getValidationMessage) => {
  if (getValidationMessage) {
    return getValidationMessage(value);
  }
  const valueLength = value ? value.length : 0;
  const charsNeeded = valueLength;
  if (valueLength && charsNeeded > 0) {
    return unescapedText('salesUI.UISearchInput.minimumSearchMessage', {
      count: charsNeeded
    });
  }
  return null;
};
const Wrapper = styled.div.withConfig({
  displayName: "SearchInput__Wrapper",
  componentId: "sc-1fy68k-0"
})(({
  theme,
  $cssStyles,
  $isDisplayOptimized
}) => {
  return Object.assign({
    position: 'relative',
    display: 'inline-block',
    width: '100%'
  }, getFocusStyleObject({
    theme,
    addBorderRadius: true
  }), {
    [`& ${StyledInput}`]: {
      paddingLeft: $isDisplayOptimized ? theme.space['0'] : theme.space['400'],
      paddingRight: $isDisplayOptimized ? theme.space['500'] : theme.space['1000'],
      // TODO : This is 40px, need a token for 44px
      borderEndEndRadius: $cssStyles.borderEndEndRadius || theme.borderRadius['transitional-full-3'],
      borderEndStartRadius: $cssStyles.borderEndStartRadius || theme.borderRadius['transitional-full-3'],
      borderStartEndRadius: $cssStyles.borderStartEndRadius || theme.borderRadius['transitional-full-3'],
      borderStartStartRadius: $cssStyles.borderStartStartRadius || theme.borderRadius['transitional-full-3']
    }
  }, $cssStyles);
});
const MessageWrapper = styled.span.withConfig({
  displayName: "SearchInput__MessageWrapper",
  componentId: "sc-1fy68k-1"
})(({
  $isDisplayOptimized,
  theme
}) => {
  return {
    position: 'absolute',
    bottom: theme.space[25],
    pointerEvents: 'none',
    top: theme.space[25],
    right: $isDisplayOptimized ? `-${theme.space[400]}` : theme.space[0]
  };
});
const MessageInner = styled.span.withConfig({
  displayName: "SearchInput__MessageInner",
  componentId: "sc-1fy68k-2"
})(({
  theme: _theme,
  $hide
}) => {
  return {
    color: _theme.color['do-not-use-eerie'],
    // TODO : token for do-not-use-eerie
    whiteSpace: 'nowrap',
    visibility: $hide ? 'hidden' : 'visible',
    position: 'relative',
    bottom: '3px'
  };
});
const IconWrapper = styled.span.withConfig({
  displayName: "SearchInput__IconWrapper",
  componentId: "sc-1fy68k-3"
})(({
  theme,
  $showClearButton
}) => {
  return {
    color: theme.color['icon-core-default'],
    wordBreak: 'normal',
    marginInline: theme.space['400'],
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    visibility: $showClearButton ? 'hidden' : 'visible',
    display: 'inline-flex',
    alignItems: 'center',
    blockSize: '100%'
  };
});
export const getStyledClickableStyles = props => {
  const {
    theme,
    $cssStyles,
    $isDisplayOptimized
  } = props;
  const styles = css(["position:absolute;right:", ";top:", ";block-size:100%;display:flex;align-items:center;justify-content:center;padding-inline:", ";", ""], $isDisplayOptimized ? theme.space[400] : theme.space[25], theme.space['25'], theme.space['400'], Object.assign({}, $cssStyles));
  return styles;
};
const StyledClickable = styled(_ref => {
  let rest = Object.assign({}, (_objectDestructuringEmpty(_ref), _ref));
  return /*#__PURE__*/_jsx(Clickable, Object.assign({}, rest));
}).withConfig({
  displayName: "SearchInput__StyledClickable",
  componentId: "sc-1fy68k-4"
})(["", ";", ""], props => getStyledClickableStyles(props), ({
  theme
}) => getFocusStyle({
  theme
}));
const TYPES = ['text', 'email', 'number', 'password', 'search', 'url', 'tel'];
export const MemoLessSearchInput = /*#__PURE__*/forwardRef((_ref2, ref) => {
  let {
      error = false,
      clearable = true,
      getValidationMessage,
      icon = true,
      onChange,
      placeholder = unescapedText('salesUI.UISearchInput.placeholder'),
      readOnly,
      value = '',
      onKeyDown,
      styled: _styled = true,
      inputRef,
      isDisplayOptimized
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded);
  const [minimumSearchMessageOverflows, setMinimumSearchMessageOverflows] = useState(false);
  const wrapperRef = useRef(null);
  const iconRef = useRef(null);
  const helpTextRef = useRef(null);
  const showClearButton = clearable && value && !readOnly;
  const minimumSearchMessage = getMinimumSearchMessage(value, getValidationMessage);
  const triggerClear = () => {
    var _inputRef$current;
    callIfPossible(onChange, syntheticEvent(''));
    inputRef === null || inputRef === void 0 || (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.focus();
  };
  const handleKeyDown = evt => {
    if (readOnly) return;
    if (evt.key === 'Escape') {
      triggerClear();
    }
    callIfPossible(onKeyDown, evt);
  };
  useEffect(() => {
    if (getValidationMessage || minimumSearchMessage) {
      const overflowResult = computeMinimumSearchMessageOverflow(wrapperRef.current, iconRef.current, helpTextRef.current, inputRef.current);
      setMinimumSearchMessageOverflows(overflowResult !== null && overflowResult !== void 0 ? overflowResult : false);
    }
  }, [getValidationMessage, inputRef, value, minimumSearchMessage]);
  const {
    tokenProps
  } = filterProps(rest);

  /**
   * Exclude padding from tokenProps to prevent padding on Wrapper AND TextInput.
   * Need to apply margin on Wrapper because icon position depends on Wrapper position, not TextInput position.
   */
  const nonPaddingTokenProps = Object.keys(tokenProps).reduce((acc, key) => {
    if (key.startsWith('padding')) {
      return acc;
    }
    acc[key] = tokenProps[key];
    return acc;
  }, {});
  const cssStyles = useMapTokenPropsToCSS(nonPaddingTokenProps);
  return /*#__PURE__*/_jsx(Tooltip, {
    open: !!(minimumSearchMessage && minimumSearchMessageOverflows),
    title: minimumSearchMessage,
    children: /*#__PURE__*/_jsxs(Wrapper, {
      "data-fnd-search-input-wrapper": "t",
      $cssStyles: cssStyles,
      $isDisplayOptimized: isDisplayOptimized,
      ref: wrapperRef,
      children: [/*#__PURE__*/_jsx(TextInput, Object.assign({
        onKeyDown: handleKeyDown,
        placeholder: lazyEval(placeholder),
        readOnly: readOnly,
        onChange: onChange,
        value: value,
        helpTextRef: helpTextRef,
        iconRef: iconRef,
        inputRef: inputRef || ref,
        error: error,
        use: "default",
        type: "search",
        role: "searchbox",
        styled: _styled
      }, getValidationMessage ? {
        minimumSearchMessage
      } : {}, rest, {
        // Margin is already applied to Wrapper
        margin: "0"
      })), /*#__PURE__*/_jsxs(MessageWrapper, {
        $isDisplayOptimized: isDisplayOptimized,
        children: [getValidationMessage && minimumSearchMessage ? /*#__PURE__*/_jsx(MessageInner, {
          ref: helpTextRef,
          $hide: minimumSearchMessageOverflows,
          children: minimumSearchMessage
        }) : null, icon && !readOnly ? /*#__PURE__*/_jsx(IconWrapper, {
          $showClearButton: showClearButton,
          ref: iconRef,
          children: /*#__PURE__*/_jsx(SearchIcon, {
            size: "300",
            color: "icon-core-subtle"
          })
        }) : null]
      }), showClearButton ? /*#__PURE__*/_jsx(StyledClickable, {
        $isDisplayOptimized: isDisplayOptimized,
        "aria-label": "Clear input",
        onClick: triggerClear,
        tabIndex: 0,
        children: /*#__PURE__*/_jsx(RemoveIcon, {
          size: "300",
          color: "icon-core-subtle"
        })
      }) : null]
    })
  });
});
export const SearchInput = ShareInput(Controllable( /*#__PURE__*/memo(MemoLessSearchInput)));
SearchInput.displayName = 'SearchInput';